import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { ApiInternal } from "src/api/api_internal";
import React, { Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "src/components/base/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { ExclamationTriangleIcon, FingerPrintIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { Badge } from "src/components/base/badge";
import WS from "src/api/ws";
import { Strong, Text } from "src/components/base/text";
import { Button } from "src/components/base/button";
import CreateOidcProviderDialog from "src/components/dialogs/create-oidc-provider";
import { ConfirmDialog } from "src/components/base/confirm-dialog";
import { notify } from "src/components/base/toast";
import { SimpleOidcProvider } from "src/api/generated/frontend_internal_v1";
import { EmptyState } from "src/components/base/empty-state";

/**
 * The properties for {@link OidcSettings}
 */
type OidcSettingsProps = {};

/**
 * The OIDC settings page
 */
function OidcSettings(props: OidcSettingsProps) {
    const [t] = useTranslation("admin-settings");
    const [tg] = useTranslation();

    const router = useRouter();
    const navigate = useNavigate();
    const { providers } = Route.useLoaderData();
    const [openCreateProvider, setOpenCreateProvider] = useState(false);
    const [deleteProvider, setDeleteProvider] = useState<SimpleOidcProvider | undefined>(undefined);

    useEffect(() => {
        const listener = WS.addEventListener("message.OidcClientChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    return (
        <div className={"flex flex-col gap-10"}>
            <div className={"flex flex-col gap-4"}>
                <div className={"flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between"}>
                    <Text>{t("description.admin-login")}</Text>
                    <Button color={"blue"} onClick={() => setOpenCreateProvider(true)}>
                        <PlusIcon />
                        {t("button.add-admin-login-method")}
                    </Button>
                </div>
                {providers.length === 0 ? (
                    <EmptyState
                        icon={<FingerPrintIcon />}
                        title={t("heading.no-admin-login")}
                        description={t("description.no-admin-login")}
                        action={
                            <Button color={"blue"} onClick={() => setOpenCreateProvider(true)}>
                                <PlusIcon />
                                {t("button.add-admin-login-method")}
                            </Button>
                        }
                    />
                ) : (
                    <Table dense={true}>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{t("label.name")}</TableHeader>
                                <TableHeader>{t("label.enable-oidc")}</TableHeader>
                                <TableHeader>{t("label.warnings")}</TableHeader>
                                <TableHeader className={"w-0"}>
                                    <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                                </TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {providers.map((provider) => (
                                <TableRow
                                    key={provider.uuid}
                                    href={`/a/settings/oidc/$providerId`}
                                    params={{ providerId: provider.uuid }}
                                >
                                    <TableCell>
                                        <Strong>{provider.name}</Strong>
                                    </TableCell>
                                    <TableCell>
                                        <Switch
                                            color={"blue"}
                                            checked={provider.enabled}
                                            onChange={async (enabled) => {
                                                await ApiInternal.oidcProvider.update(provider.uuid, { enabled });
                                                await router.invalidate();
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <div className={"w-40"}>
                                            {provider.enabled &&
                                                (!provider.client_secret ||
                                                    provider.url.length === 0 ||
                                                    provider.redirect_url.length === 0 ||
                                                    provider.client_id.length === 0) && (
                                                    <Badge color={"yellow"}>
                                                        <ExclamationTriangleIcon className={"size-4"} />
                                                        {t("label.missing-configuration")}
                                                    </Badge>
                                                )}
                                            {provider.errors &&
                                                !(
                                                    !provider.client_secret ||
                                                    provider.url.length === 0 ||
                                                    provider.redirect_url.length === 0 ||
                                                    provider.client_id.length === 0
                                                ) && (
                                                    <Badge color={"red"}>
                                                        <ExclamationTriangleIcon className={"size-4"} />
                                                        {t("label.oidc-errors")}
                                                    </Badge>
                                                )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Button plain={true} onClick={() => setDeleteProvider(provider)}>
                                            <TrashIcon />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>

            <Suspense>
                <CreateOidcProviderDialog
                    isOpen={openCreateProvider}
                    onCreate={async (provider_id) => {
                        setOpenCreateProvider(false);
                        notify.success(t("toast.create-successfully"));
                        await navigate({ to: "/a/settings/oidc/$providerId", params: { providerId: provider_id } });
                    }}
                    onClose={() => setOpenCreateProvider(false)}
                />
                <ConfirmDialog
                    open={deleteProvider !== undefined}
                    onClose={() => setDeleteProvider(undefined)}
                    onConfirm={async () => {
                        await ApiInternal.oidcProvider.delete(deleteProvider!.uuid);
                        notify.success(t("toast.deleted-successfully"));
                        setDeleteProvider(undefined);
                        await router.invalidate();
                    }}
                    title={t("heading.delete-oidc-provider", { provider: deleteProvider?.name ?? "" })}
                    description={t("description.delete-oidc-provider")}
                    confirmLabel={t("button.delete-oidc-provider")}
                />
            </Suspense>
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/settings/_settings/oidc/")({
    component: OidcSettings,
    loader: async () => {
        const [{ list: providers }] = await Promise.all([ApiInternal.oidcProvider.getAll()]);
        return { providers };
    },
});
